type TimeUnit = {
  value: number
  unit: string
}

/**
 * Converts ISO 8601 duration string to seconds
 */
export function parseISODuration(duration: string): number {
  // Check if duration is negative
  const isNegative = duration.startsWith('-')
  // Remove negative sign for parsing
  const cleanDuration = isNegative ? duration.slice(1) : duration

  const matches = cleanDuration.match(/P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?/)
  if (!matches) return 0

  const [_, days, hours, minutes, seconds] = matches

  const totalSeconds =
    parseInt(days || '0') * 24 * 60 * 60 +
    parseInt(hours || '0') * 60 * 60 +
    parseInt(minutes || '0') * 60 +
    parseFloat(seconds || '0')

  // Return negative value if original duration was negative
  return isNegative ? -totalSeconds : totalSeconds
}

/**
 * Formats a time delta (in seconds) into a human-readable string
 * @param input - Input string or number of seconds
 * @param options - Formatting options
 * @returns Formatted string (e.g., "2 hours ago", "5 minutes ago", "just now")
 */
export function formatTimeDelta(
  input: string | number,
  options: {
    short?: boolean
    future?: boolean
    threshold?: number
    roundUp?: boolean
  } = {},
) {
  const seconds = typeof input === 'string' ? parseISODuration(input) : input
  const { short = true, future = true, threshold = 60, roundUp = true } = options

  // For future times, if less than a minute, show "1m"
  if (future && seconds < 60) {
    return short ? '1M' : '1 minute remaining'
  }

  // For past times or non-future context, show "just now" if under threshold
  if (!future && Math.abs(seconds) < threshold && seconds >= 0) {
    return 'just now'
  }

  const timeUnits: TimeUnit[] = [
    { value: 31536000, unit: 'year' },
    { value: 2592000, unit: 'month' },
    { value: 604800, unit: 'week' },
    { value: 86400, unit: 'day' },
    { value: 3600, unit: 'hour' },
    { value: 60, unit: 'minute' },
    { value: 1, unit: 'second' },
  ]

  const absSeconds = Math.abs(seconds)
  const isNegative = seconds < 0

  for (let i = 0; i < timeUnits.length; i++) {
    const { value, unit } = timeUnits[i]
    if (absSeconds >= value) {
      const exactCount = absSeconds / value
      let count: number

      // Determine if we should check the next smaller unit for rounding
      const shouldCheckNextUnit = i < timeUnits.length - 1 && roundUp
      if (shouldCheckNextUnit) {
        const nextUnit = timeUnits[i + 1]
        const remainder = absSeconds % value
        const nextUnitCount = remainder / nextUnit.value
        // Round up if the remainder is more than 70% of the next unit
        if (nextUnitCount >= 0.7) {
          count = Math.ceil(exactCount)
        } else {
          count = Math.floor(exactCount)
        }
      } else {
        // Use ceiling for future times (deadlines) and floor for past times if roundUp is true
        count = roundUp && future ? Math.ceil(exactCount) : Math.floor(exactCount)
      }

      const signedCount = isNegative ? -count : count

      if (short) {
        // Short format (e.g., "2D", "5H", "30M", "-2D", "-5H")
        const shortUnit = unit[0].toUpperCase()
        return `${signedCount}${shortUnit}`
      }

      // Long format with past/future handling
      const pluralUnit = Math.abs(signedCount) === 1 ? unit : `${unit}s`
      if (future) {
        return isNegative ? `${Math.abs(signedCount)} ${pluralUnit} overdue` : `${signedCount} ${pluralUnit} left`
      }
      return `${signedCount} ${pluralUnit} ago`
    }
  }

  return seconds < 0 ? '-1M' : 'just now'
}
